import { DashboardFrame } from '@aleph/supergrid';
import {
	alephToast, NotificationToast, NotificationType, StatusToggle,
} from '@aleph/shared-components';
import { FC, useMemo } from 'react';
import {
	GenericStatus,
	SpotifyLevel,
	STATUS_CHECKED_VALUES,
	STATUS_COLOR_PALETTES,
	SYNC_PENDING_STATUS_COLOR_PALETTES,
} from 'wb-spotify/app/routes/superGrid/utils';
import { ICellRendererParams } from 'ag-grid-community';
import { useMutation } from '@apollo/client';
import {
	collectChildrenInDraftStatus,
	getVariablesForActivateDraftEntities,
	isEntitySyncPending,
	SPOTIFY_DRAFT_STATUSES,
	SPOTIFY_LEVEL_TO_ACTIVE_STATUS_MAP,
	updateChildrenStatusesInGrid,
} from 'wb-spotify/app/routes/superGrid/cellRenderers/utils/status';
import {
	ActivateDraftEntitiesDocument,
	CampaignStatus,
	UpdateCampaignStatusDocument,
} from 'wb-spotify/graphql/generated';
import { captureException } from '@sentry/react';
import { useTranslation } from 'react-i18next';

interface IStatusCellRendererParams extends ICellRendererParams {
	statusPending?: GenericStatus;
}

const StatusCellRenderer: FC<IStatusCellRendererParams> = ({
	value, node, data, api,
}) => {
	const { t } = useTranslation();
	const isDashboardView = data?.isDashboardView && !node.expanded;
	const [updateCampaignStatus, { loading: isUpdatingCampaignStatus }] = useMutation(
		UpdateCampaignStatusDocument,
		{ errorPolicy: 'all' },
	);
	const [activateDraftEntities, { loading: isActivatingDraftEntities }] = useMutation(
		ActivateDraftEntitiesDocument,
		{ errorPolicy: 'all' },
	);

	const isSyncPending = useMemo(() => {
		if (data?.level === SpotifyLevel.AD_ACCOUNT || SPOTIFY_DRAFT_STATUSES.includes(data?.status)) {
			return false;
		}

		return isEntitySyncPending(data);
	}, [data?.level, data?.externalId, data]);

	const statusColorPalette = useMemo(() => {
		if (isSyncPending) {
			return SYNC_PENDING_STATUS_COLOR_PALETTES[data?.supergridLevel];
		}

		return STATUS_COLOR_PALETTES[data?.supergridLevel];
	}, [isSyncPending, data?.supergridLevel]);

	const currAndParentDraft = useMemo(
		() => SPOTIFY_DRAFT_STATUSES.includes(data?.status) && SPOTIFY_DRAFT_STATUSES.includes(data?.parent?.status),
		[data?.status, data?.parent?.status],
	);

	const isDisabled = useMemo(() => {
		if (data?.level === SpotifyLevel.AD_ACCOUNT) {
			return true;
		}

		const isCreateOperation = data?.isCreate;
		const isInDashboardView = data?.isDashboardView;
		const hasPendingSync = isSyncPending;
		const hasDraftParent = currAndParentDraft;
		const isCurrentlyLoading = isUpdatingCampaignStatus || isActivatingDraftEntities;

		const baseDisabledConditions = isCreateOperation
			|| isInDashboardView
			|| hasPendingSync
			|| hasDraftParent
			|| isCurrentlyLoading;

		if (data?.level === SpotifyLevel.CAMPAIGN) {
			return baseDisabledConditions;
		}

		const isDraftStatus = SPOTIFY_DRAFT_STATUSES.includes(data?.status);

		return baseDisabledConditions || !isDraftStatus;
	}, [
		data?.isCreate,
		data?.isDashboardView,
		data?.status,
		data?.level,
		data?.parent?.status,
		isSyncPending,
		currAndParentDraft,
		isUpdatingCampaignStatus,
		isActivatingDraftEntities,
	]);

	const tooltipContent = useMemo(() => {
		if (currAndParentDraft) {
			return 'Parent level is in draft status';
		}

		if (isSyncPending) {
			return 'Pending sync to Spotify';
		}

		return undefined;
	}, [isSyncPending, currAndParentDraft]);

	const handleChangeStatus = async () => {
		if (SPOTIFY_DRAFT_STATUSES.includes(data?.status)) {
			const variables = getVariablesForActivateDraftEntities(data);

			if (variables?.campaigns?.length || variables?.groups?.length || variables?.ads?.length) {
				// Collect all children in draft status before mutations
				const childrenInDraft = collectChildrenInDraftStatus(data?.children);

				const { errors } = await activateDraftEntities({
					variables,
				});

				if (errors) {
					captureException(errors);

					const errorMessages = errors.map(error => {
						const errMsgs: string[] = [];
						// @ts-ignore
						const fieldKeys = Object.keys(error?.form ?? {});

						fieldKeys.forEach(key => {
							// @ts-ignore
							errMsgs.push(t(error?.form?.[key]?.message ?? '', 'Unknown error'));
						});

						return errMsgs.join(', ');
					});

					alephToast(
						<NotificationToast type={NotificationType.Error}
							title={
								errorMessages.length
									? errorMessages.join(', ')
									// TODO: add a default key for the error message
									: t('some default key', 'Something went wrong when changing the entity status')
							}
						/>,
					);

					return;
				}

				// Update the clicked entity status
				const route = node.getRoute()?.slice(0, -1);

				api.applyServerSideTransaction({
					route,
					update: [{
						...data,
						status: SPOTIFY_LEVEL_TO_ACTIVE_STATUS_MAP[data?.supergridLevel],
					}],
				});

				// Update children statuses in the grid
				if (childrenInDraft.length > 0) {
					updateChildrenStatusesInGrid(childrenInDraft, api, node);
				}

				alephToast(
					<NotificationToast
						type={NotificationType.Success}
						title="Entity statuses were changed successfully"
					/>,
				);
			}
		} else if (data?.level === SpotifyLevel.CAMPAIGN) {
			const newCampaignStatus = (
				data?.status === CampaignStatus.Active
					? CampaignStatus.Paused
					: CampaignStatus.Active
			);
			const { errors } = await updateCampaignStatus({
				variables: {
					campaignId:     data?.id,
					campaignStatus: newCampaignStatus,
					campaignHash:   data?.id,
				},
			});

			if (errors) {
				captureException(errors);
				alephToast(
					<NotificationToast type={NotificationType.Error}
						title="Something went wrong when changing the campaign status"
					/>,
				);

				return;
			}

			api.applyServerSideTransaction({
				route:  node.getRoute()?.slice(0, -1),
				update: [{
					...data,
					status: newCampaignStatus,
				}],
			});

			alephToast(
				<NotificationToast
					type={NotificationType.Success}
					title="Campaign status was changed successfully"
				/>,
			);
		}
	};

	if (data?.isCreate) {
		return null;
	}

	return (
		<>
			{
				<div className="w-full flex justify-center h-[64px] max-h-full items-center cursor-not-allowed">
					<StatusToggle
						onClick={handleChangeStatus}
						currentStatus={value}
						colorPalette={statusColorPalette}
						disabled={isDisabled}
						checkedStatuses={STATUS_CHECKED_VALUES[data?.supergridLevel]}
						tooltipContent={tooltipContent}
					/>
				</div>
			}
			{isDashboardView && <DashboardFrame />}
		</>
	);
};

export default StatusCellRenderer;
